import _ from 'lodash';

import React, { useCallback, useMemo } from 'react';
import createClass from 'create-react-class';
import { withTranslation } from 'react-i18next';

import { NavLink } from 'react-router-dom';

import ProfileComponent from './ProfileComponent.react';
import TitleManager from '../../../../utils/TitleManager';

import BasketComponent from './basket/BasketComponent.react';

import DisplayCurrencyComponent from './display-currency/DisplayCurrencyComponent.react';

import CombtasBar from './CombtasBar';
import LanguageSwitcher from './LanguageSwitcher/LanguageSwitcher';

import Config from 'arbitrip-common/client/utils/Config';
import DocumentTitle from '../../../general/DocumentTitle.react';

import Analytics from 'arbitrip-common/client/analytics';
import { UTM_SOURCE_PARAMETER } from 'arbitrip-common/client/constants/ProfileConstants';
import { LOGOS } from 'arbitrip-common/client/constants/BrandConstants';
import { getIsracardBaseUrl, getIsracardFlightsUrl } from 'arbitrip-common/client/utils/IsracardUrls';
import { getLetstayPartnerLogoUrl } from 'arbitrip-common/client/utils/letstayPartnerLogo';
import { addUtmDataToUrl } from 'arbitrip-common/client/utils/UtmUtils';
import MemberComponent from './MemberComponent';
import { isIsracardCompany, isElalCompany, isAirHaifaCompany } from '@/utils/isCompany';
import Button from '@mui/material/Button';
import { ReactComponent as LoginIcon } from '@/img/icons/login.svg';
import { ReactComponent as MemberIcon } from '@/img/icons/member-icon.svg';
import LinkToSite from './LinkToSite';
import RouterWrapper from '@/utils/RouterWrapper';
import { buildLoginUrl } from '@/utils/LoginUrl';

const LayoutRenderer = ({ config = [], containerClass, t }) => {
    return (
        <div className={containerClass}>
            {config
                .filter((item) => !item?.condition || item.condition?.())
                .map((item, index) => {
                    if (item.type === 'navlink') {
                        const { wrapperClass, children, ...props } = item;

                        return (
                            <div className={wrapperClass || ''} key={props.key || index}>
                                <NavLink {...props}>{typeof children === 'string' ? t(children) : children}</NavLink>
                            </div>
                        );
                    }

                    if (item.type === 'component') {
                        const { wrapperClass, component: Component, componentProps, ...rest } = item;

                        return (
                            <div className={wrapperClass || ''} key={index} {...rest}>
                                <Component {...(componentProps || {})} />
                            </div>
                        );
                    }

                    return null;
                })}
        </div>
    );
};

function invertColors() {
    document.documentElement.style.webkitFilter = document.documentElement.style.webkitFilter ? '' : 'invert(100%)';
}

function reloadCss() {
    let h, a, f;
    a = document.getElementsByTagName('link');
    for (h = 0; h < a.length; h++) {
        f = a[h];
        if (f.rel.toLowerCase().match(/stylesheet/) && f.href) {
            const g = f.href.replace(/(&|%5C?)forceReload=\d+/, '');
            f.href = g + (g.match(/\?/) ? '&' : '?') + 'forceReload=' + new Date().valueOf();
        }
    }
}

const TopBar = (props) => {
    const { t, i18n } = props;
    const profile = props.user;

    const isElal = isElalCompany();
    const isIsracard = isIsracardCompany();
    const isAirHaifa = isAirHaifaCompany();
    const isracardBaseUrl = getIsracardBaseUrl();
    const isracardFlightsUrl = getIsracardFlightsUrl();
    const rootUrl = isIsracard ? addUtmDataToUrl(isracardBaseUrl, isIsracard) : '/';
    const searchUrl = RouterWrapper.buildSearchRoute();

    const COMPANY_NAME = _.get(props, 'user.partner_login_company_name', false) || props.company.name;
    let title = TitleManager.defaults()
        ? TitleManager.buildTitleEndsWith('for ' + COMPANY_NAME, false)
        : TitleManager.buildTitleEndsWith('for ' + COMPANY_NAME, false);

    const LOGO_CLASSES = ['image', Config.dev_mode && 'dead-logo', Config.is_letstay && 'letstay', isElal && 'elal']
        .filter(Boolean)
        .join(' ');

    const SHOW_HOTEL_BOOKING_BUTTONS =
        !props.company.settings.management_enabled ||
        props.company.settings.employees_hotel_booking_enabled ||
        profile.travel_manager;

    const { hide_basket, hide_brand, hide_user_menu, force_hebrew_only } = _.get(profile, 'company.partners_ui', {});
    const loginUrl = buildLoginUrl({ filterResultsRefUrl: isElal });

    const renderLoginButton = useCallback(
        () =>
            isElal ? (
                <div className="member-component">
                    <Button
                        className="member-component__button tab-theme-color"
                        disableRipple
                        component="a"
                        href={loginUrl}
                        data-testid="login-button"
                    >
                        <MemberIcon aria-hidden="true" />

                        <div className="member-component__info">
                            <span className="member-component__name">{t('Login')}</span>
                        </div>
                    </Button>
                </div>
            ) : (
                <Button
                    className="login-button tab-theme-color"
                    variant="outlined"
                    disableRipple
                    component="a"
                    href={loginUrl}
                    data-testid="login-button"
                >
                    <span>{t('Login')}</span>
                    <LoginIcon />
                </Button>
            ),
        [isElal, loginUrl, t],
    );

    let img_title = 'Arbitrip.com';
    let BASE_LOGO_IMG = <img src={LOGOS.ARBITRIP} alt={img_title} className={LOGO_CLASSES} />;

    if (props.company.custom_header_logo) {
        img_title = props.company.custom_header_alt;
        BASE_LOGO_IMG = <img src={props.company.custom_header_logo} alt={img_title} className={LOGO_CLASSES} />;
    }

    // Use the partner logo from utm_source if available
    const URLParams = new URLSearchParams(document.location.search);
    const utmSource = URLParams.get(UTM_SOURCE_PARAMETER);
    const partnerLogo = getLetstayPartnerLogoUrl(utmSource);

    const renderLogoComponent = useMemo(
        () => (
            <div className="logo">
                {BASE_LOGO_IMG}
                {partnerLogo ? (
                    <>
                        <span className="logo-with-text">{t('With')}</span>
                        <img src={partnerLogo} alt={utmSource} className={LOGO_CLASSES} />
                    </>
                ) : null}
            </div>
        ),
        [partnerLogo, t, BASE_LOGO_IMG, LOGO_CLASSES, utmSource],
    );

    const leftLayout = useMemo(
        () => [
            {
                type: 'navlink',
                to: rootUrl,
                title: img_title,
                id: 'logo',
                className: 'link',
                onClick: () => Analytics.actions.interactions.logoClicked(profile),

                wrapperClass: '',

                children: renderLogoComponent,
            },
        ],
        [img_title, profile, renderLogoComponent, rootUrl],
    );

    const middleLayout = useMemo(
        () => [
            {
                type: 'navlink',
                condition: () => props.company.settings.management_enabled,
                wrapperClass: 'company-bar-div navigation-div',
                to: '/trips',
                className: 'hvr-underline-from-center',
                children: 'Navigation.Trips',
            },
            {
                type: 'navlink',
                condition: () => SHOW_HOTEL_BOOKING_BUTTONS && isIsracard,
                wrapperClass: 'company-bar-div navigation-div',
                className: 'hvr-underline-from-center',
                to: t('FlightsUrl'),
                target: '_blank',
                rel: 'noopener noreferrer',
                onClick: () => Analytics.actions.interactions.flightsClicked(profile),
                children: 'Navigation.Flights',
            },
            {
                type: 'navlink',
                condition: () => SHOW_HOTEL_BOOKING_BUTTONS,
                wrapperClass: 'company-bar-div navigation-div',
                to: searchUrl,
                className: 'hvr-underline-from-center',
                onClick: () => Analytics.actions.interactions.searchClicked(profile),
                children: props.company.settings.management_enabled ? 'Navigation.Hotels' : 'Navigation.Search',
            },
            {
                type: 'navlink',
                condition: () => SHOW_HOTEL_BOOKING_BUTTONS,
                wrapperClass: 'company-bar-div navigation-div',
                to: '/bookings',
                className: 'hvr-underline-from-center',
                onClick: () => Analytics.actions.interactions.bookingsClicked(profile),
                'data-testid': 'booking-nav-link',
                children: 'Navigation.Bookings',
            },
            {
                type: 'navlink',
                condition: () => profile.approval_flow_enabled,
                wrapperClass: 'company-bar-div navigation-div',
                to: '/requests',
                className: 'hvr-underline-from-center',
                children: t('Navigation.Requests'),
            },

            {
                type: 'navlink',
                condition: () => profile.reports_dashboard,
                wrapperClass: 'company-bar-div navigation-div',
                to: '/dashboard',
                className: 'hvr-underline-from-center',
                children: 'Navigation.Dashboard',
            },

            {
                type: 'component',
                condition: () => isElal,
                wrapperClass: 'company-bar-div',
                component: LinkToSite,
            },
        ],
        [
            props.company.settings.management_enabled,
            SHOW_HOTEL_BOOKING_BUTTONS,
            profile,
            isElal,
            searchUrl,
            hide_basket,
            t,
            i18n.language,
        ],
    );

    const middleLayoutIsracard = useMemo(
        () => [
            {
                type: 'navlink',
                condition: () => SHOW_HOTEL_BOOKING_BUTTONS,
                wrapperClass: 'company-bar-div navigation-div',
                className: 'hvr-underline-from-center',
                to: addUtmDataToUrl(isracardFlightsUrl, isIsracard),
                target: '_blank',
                rel: 'noopener noreferrer',
                onClick: () => Analytics.actions.interactions.flightsClicked(profile),
                children: 'Navigation.Flights',
            },
            {
                type: 'navlink',
                condition: () => SHOW_HOTEL_BOOKING_BUTTONS,
                wrapperClass: 'company-bar-div navigation-div',
                to: searchUrl,
                className: 'hvr-underline-from-center',
                onClick: () => Analytics.actions.interactions.searchClicked(profile),
                children: 'Navigation.Hotels',
            },
            {
                type: 'navlink',
                condition: () => SHOW_HOTEL_BOOKING_BUTTONS,
                wrapperClass: 'company-bar-div navigation-div',
                to: addUtmDataToUrl(`${isracardBaseUrl}/account-orders`, isIsracard),
                target: '_blank',
                rel: 'noopener noreferrer',
                onClick: () => Analytics.actions.interactions.bookingsClicked(profile),
                'data-testid': 'booking-nav-link',
                children: 'Navigation.Bookings',
            },
            {
                type: 'navlink',
                wrapperClass: 'company-bar-div navigation-div',
                to: addUtmDataToUrl(`${isracardBaseUrl}/faq`, isIsracard),
                target: '_blank',
                rel: 'noopener noreferrer',
                className: 'hvr-underline-from-center',
                children: 'Navigation.Faq',
            },
            {
                type: 'navlink',
                wrapperClass: 'company-bar-div navigation-div',
                to: addUtmDataToUrl(`${isracardBaseUrl}/about`, isIsracard),
                target: '_blank',
                rel: 'noopener noreferrer',
                className: 'hvr-underline-from-center',
                children: 'Navigation.AboutUs',
            },
            {
                type: 'navlink',
                wrapperClass: 'company-bar-div navigation-div',
                to: addUtmDataToUrl(`${isracardBaseUrl}/contact-us`, isIsracard),
                target: '_blank',
                rel: 'noopener noreferrer',
                className: 'hvr-underline-from-center',
                children: 'Navigation.ContactUs',
            },
        ],
        [SHOW_HOTEL_BOOKING_BUTTONS, isracardBaseUrl, isracardFlightsUrl, isIsracard, profile, searchUrl, t],
    );

    const middleLayoutAirHaifa = useMemo(
        () => [
            {
                type: 'navlink',
                wrapperClass: 'company-bar-div navigation-div',
                className: 'hvr-underline-from-center',
                to: 'https://www.airhaifa.com/?utm_source=Arbihotels',
                target: '_blank',
                rel: 'noopener noreferrer',
                onClick: () => Analytics.actions.interactions.flightsClicked(profile),
                children: 'Navigation.Flights',
            },
            {
                type: 'navlink',
                wrapperClass: 'company-bar-div navigation-div',
                to: searchUrl,
                className: 'hvr-underline-from-center',
                onClick: () => Analytics.actions.interactions.searchClicked(profile),
                children: 'Navigation.Hotels',
            },
            {
                type: 'navlink',
                wrapperClass: 'company-bar-div navigation-div',
                to: '/bookings',
                className: 'hvr-underline-from-center',
                onClick: () => Analytics.actions.interactions.bookingsClicked(profile),
                'data-testid': 'booking-nav-link',
                children: 'Navigation.Bookings',
            },
        ],
        [profile, searchUrl],
    );

    const rightLayout = useMemo(
        () => [
            {
                type: 'component',
                condition: () => !hide_basket && SHOW_HOTEL_BOOKING_BUTTONS && !isElal,
                wrapperClass: 'company-bar-div basket-div',
                ariaLabel: 'Send hotels for review',
                component: BasketComponent,
                componentProps: { profile },
            },
            {
                type: 'component',
                wrapperClass: 'company-bar-div display-currency-div',
                component: DisplayCurrencyComponent,
                componentProps: {
                    profile,
                    reservationPage: props.reservationPage,
                },
            },

            {
                type: 'component',
                condition: () => profile.private_travel_only && !force_hebrew_only,
                component: LanguageSwitcher,
                componentProps: {
                    changeLanguage: props.changeLanguage,
                    updateLanguageOnDB: props.updateLanguageOnDB,
                },
            },

            {
                type: 'component',
                condition: () => !hide_brand && !isElal,
                wrapperClass: 'company-bar-div company-name-div',
                component: () => (
                    <div
                        title={COMPANY_NAME}
                        onClick={Config.localhost ? invertColors : null}
                        onDoubleClick={Config.localhost ? reloadCss : null}
                    >
                        {COMPANY_NAME}
                    </div>
                ),
            },

            {
                type: 'component',
                condition: () => !hide_user_menu && !isElal,
                wrapperClass: 'company-bar-div user-name-div',
                component: () =>
                    profile.anonymous && !profile.isLoggedIn ? renderLoginButton() : <ProfileComponent />,
            },

            {
                type: 'component',
                condition: () => isElal,
                component: () =>
                    profile.anonymous && !profile.isLoggedIn ? (
                        renderLoginButton()
                    ) : (
                        <MemberComponent profile={profile} />
                    ),
            },
        ],
        [profile, renderLoginButton],
    );

    return (
        <DocumentTitle title={title}>
            <div className="top-bar" data-testid="header-container">
                <div className="company-bar">
                    <div className="inner-company-bar">
                        <div className="left-layout">
                            <LayoutRenderer config={leftLayout} containerClass="company-bar-div logo-div" t={t} />
                        </div>

                        <div className={`middle-layout ${isIsracard ? 'isracard-layout' : 'default-layout'}`}>
                            <LayoutRenderer
                                config={isIsracard ? middleLayoutIsracard : isAirHaifa ? middleLayoutAirHaifa : middleLayout}
                                containerClass="navigation-container"
                                t={t}
                            />
                        </div>

                        <div className="right-layout">
                            <LayoutRenderer config={rightLayout} containerClass="profile-container" t={t} />
                        </div>
                    </div>

                    <hr className="shadower" />
                </div>

                {props.showCombtasBar && <CombtasBar endCombtasSession={props.endCombtasSession} tasId={props.tasId} />}
            </div>
        </DocumentTitle>
    );
};

export default withTranslation()(TopBar);
